import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { authClient } from '../lib/auth-client';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const tokenError = searchParams.get('error');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (tokenError || !token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--color-surface-container-low)] px-6">
        <div className="w-full max-w-sm space-y-8">
          <div className="rounded-3xl bg-card p-8 shadow-ambient text-center space-y-4">
            <h1 className="display-tight text-2xl font-semibold">Link expired</h1>
            <p className="text-sm text-muted-foreground">
              This reset link is invalid or has expired. Request a new one.
            </p>
            <Link
              to="/forgot-password"
              className="btn-primary-gradient inline-flex h-11 items-center justify-center rounded-full px-6 text-sm font-medium text-white shadow-ambient"
            >
              Request new link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const result = await authClient.resetPassword({ newPassword: password, token });
      if (result.error) {
        setError(result.error.message || 'Could not reset password');
        setLoading(false);
        return;
      }
      navigate('/login?reset=1', { replace: true });
    } catch {
      setError('An unexpected error occurred');
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-surface-container-low)] px-6">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center">
          <h1 className="display-tight text-3xl font-semibold">Set a new password</h1>
        </div>

        <div className="rounded-3xl bg-card p-8 shadow-ambient">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="rounded-2xl bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="password" className="label-caps text-xs text-muted-foreground">
                New password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="flex h-10 w-full rounded-2xl border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-lowest)] px-4 py-2 text-sm transition-all placeholder:text-muted-foreground focus:bg-[var(--color-surface-bright)] focus:shadow-[0_4px_16px_rgba(49,51,47,0.06)] focus:outline-none"
                placeholder="At least 8 characters"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="confirm" className="label-caps text-xs text-muted-foreground">
                Confirm password
              </label>
              <input
                id="confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={8}
                className="flex h-10 w-full rounded-2xl border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-lowest)] px-4 py-2 text-sm transition-all placeholder:text-muted-foreground focus:bg-[var(--color-surface-bright)] focus:shadow-[0_4px_16px_rgba(49,51,47,0.06)] focus:outline-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary-gradient inline-flex h-11 w-full items-center justify-center rounded-full px-6 text-sm font-medium text-white shadow-ambient transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50"
            >
              {loading ? 'Updating...' : 'Update password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
